import { useEffect, useSyncExternalStore } from "react";
import {
  ATTENTION_PRIORITY,
  claimAttention,
  getAttentionSnapshot,
  subscribeAttention,
  type AttentionKey,
} from "@/lib/orchestration/attentionOrchestrator";

/**
 * Live snapshot of the current dominant attention focus. Re-renders
 * subscribers whenever the focus changes — used by secondary surfaces
 * (Story Arc, Nation Rivalry, World Status, ribbons) to fade / blur
 * themselves when a higher-priority surface owns the screen.
 */
export function useAttentionFocus() {
  return useSyncExternalStore(
    subscribeAttention,
    getAttentionSnapshot,
    getAttentionSnapshot,
  );
}

/**
 * Returns true when the caller should visually yield (blur + dim) because
 * a higher-priority surface owns the screen. Self-priority defaults to
 * the ATTENTION_PRIORITY for `selfKey`.
 */
export function useAttentionYield(selfKey: AttentionKey | null): boolean {
  const snap = useAttentionFocus();
  if (!snap.active || !selfKey) return false;
  if (snap.key === selfKey) return false;
  return snap.priority > ATTENTION_PRIORITY[selfKey];
}

/**
 * Effect helper — claim dominant focus while `active` is true, auto
 * releases on unmount or when `active` flips false. TTL is a safety net.
 */
export function useClaimAttention(
  key: AttentionKey,
  active: boolean,
  ttlMs = 8_000,
): void {
  useEffect(() => {
    if (!active) return;
    const claim = claimAttention(key, ttlMs);
    return () => { claim?.release(); };
  }, [key, active, ttlMs]);
}